import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useState } from "react";
import { cn } from "@/core/helpers/cn";
import { Button, Icon } from "@/shared/ui";

type Props = {
  src: string;
  activeIndex?: number;
  setActiveIndex?: (activeIndex: number) => void;
  list: unknown[];
  children?: ReactNode;
  classNameContainer?: string;
  classNameLeftBtn?: string;
  classNameRightBtn?: string;
  isHideButtons?: boolean;
};

type Direction = "left" | "right";

export default function CarouselSlider({
  src,
  activeIndex,
  setActiveIndex,
  list,
  children,
  classNameContainer,
  classNameLeftBtn,
  classNameRightBtn,
  isHideButtons,
}: Props) {
  const [direction, setDirection] = useState<Direction>("right");

  const variants = {
    enter: (dir: Direction) => ({
      x: dir === "right" ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: Direction) => ({
      x: dir === "right" ? -1000 : 1000,
      opacity: 0,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection: Direction) => {
    if (!isHideButtons) {
      setDirection(newDirection);
      if (newDirection === "right") {
        setActiveIndex(activeIndex === list.length - 1 ? 0 : activeIndex + 1);
      } else {
        setActiveIndex(activeIndex === 0 ? list.length - 1 : activeIndex - 1);
      }
    }
  };

  return (
    <div className={cn("relative w-full h-full overflow-clip rounded-4", classNameContainer)}>
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={activeIndex}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);
            if (swipe < -swipeConfidenceThreshold) {
              paginate("right");
            } else if (swipe > swipeConfidenceThreshold) {
              paginate("left");
            }
          }}
          className="absolute flex flex-col justify-between size-full rounded-[1rem] overflow-hidden"
        >
          <div
            className="relative flex size-full bg-dark-800"
            style={{
              backgroundImage: `url(${src})`,
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
            }}
          >
            {children}
          </div>
        </motion.div>
      </AnimatePresence>

      {!isHideButtons && (
        <>
          <Button
            variant="solid"
            color="matched"
            className={cn(
              "absolute z-2 top-1/2 left-4 h-8.5 w-8.5 p-0 -translate-y-[50%]",
              classNameLeftBtn,
            )}
            onClick={() => paginate("left")}
            disabled={activeIndex === 0}
          >
            <Icon name="triangle-right" className="w-2.5 rotate-180" />
          </Button>
          <Button
            variant="solid"
            color="matched"
            className={cn(
              "absolute z-2 top-1/2 right-4 h-8.5 w-8.5 p-0 -translate-y-[50%]",
              classNameRightBtn,
            )}
            onClick={() => paginate("right")}
            disabled={activeIndex === list.length - 1}
          >
            <Icon name="triangle-right" className="w-2.5" />
          </Button>
        </>
      )}
    </div>
  );
}
